import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import {
  CopyTemplate,
  CreateTemplate,
  EnsureOwnerTemplates,
  ImportBoardTemplate,
  UpdateTemplate,
} from 'modules/core/domain/interfaces/supabase/overlay/auxiliary-types';
import { Board, Template } from 'modules/core/domain/interfaces/supabase/overlay/entity';
import supabase from 'modules/core/supabase/supabase';
import { boardTemplateRemoved, boardTemplateSaved } from 'modules/template-editor/store/templates/templates';
import { baseApi } from 'shared/api/base-api';
import { toApiError } from 'shared/errors';

export const BOARD_TEMPLATE_TAG = 'BoardTemplate' as const;

/** Аргумент списка: организация и видимость (`getBoardTemplateVisibility`) — вместе это ключ кэша. */
export type OwnerBoardTemplatesArgs = Required<EnsureOwnerTemplates>;

type Dispatch = ThunkDispatch<any, unknown, AnyAction>;

/**
 * Аргументы всех закэшированных списков шаблонов: шаблон может войти в один и уйти из другого. Публичный
 * `selectInvalidatedBy` — тот же отбор по тегу, что делает инвалидация, без чтения внутренней формы
 * `state.api.queries` ([RTK Query — API Slice Utilities](https://redux-toolkit.js.org/rtk-query/api/created-api/api-slice-utils#selectinvalidatedby)).
 * Состояние приходит как `unknown`: тип стора RTK выводит из самого `boardTemplatesApi`, а объявить его здесь —
 * значит замкнуть вывод типа API на самого себя.
 */
function cachedOwnerListArgs(state: unknown): OwnerBoardTemplatesArgs[] {
  type ApiState = Parameters<typeof boardTemplatesApi.util.selectInvalidatedBy>[0];
  return boardTemplatesApi.util
    .selectInvalidatedBy(state as ApiState, [BOARD_TEMPLATE_TAG])
    .filter((entry) => entry.endpointName === 'getOwnerBoardTemplates')
    .map((entry) => entry.originalArgs as OwnerBoardTemplatesArgs);
}

/**
 * Записать созданный или изменённый шаблон в кэш списков — так же синхронно, как `putSettingsIntoCache`
 * (`.claude/rules/api-and-state.md`, «A mutation that returns the whole entity writes it into the query cache»):
 * без второго запроса и без мигания прежним списком. Шаблон входит в список, если он этой организации и его
 * `visibility` в аргументах; иначе (стал общим, сменил видимость) — уходит из него.
 */
function putTemplateIntoCache(template: Template, dispatch: Dispatch, getState: () => unknown) {
  for (const args of cachedOwnerListArgs(getState())) {
    const belongs = template.ownerId === args.ownerId && args.visibility.includes(template.visibility);
    dispatch(
      boardTemplatesApi.util.updateQueryData('getOwnerBoardTemplates', args, (draft) => {
        const at = draft.findIndex((item) => item.id === template.id);
        if (belongs && at >= 0) draft[at] = template;
        else if (belongs) draft.push(template);
        else if (at >= 0) draft.splice(at, 1);
      }),
    );
  }
  dispatch(boardTemplateSaved(template));
}

/**
 * Шаблон удалён — убрать его из всех списков и из слайса. Экспортируется для повтора с кодом 15: первый запрос
 * шаблон уже удалил, и экран приводится к тому, что дал бы успех.
 */
export const removeBoardTemplateFromCache = (id: number) => (dispatch: Dispatch, getState: () => unknown) => {
  for (const args of cachedOwnerListArgs(getState())) {
    dispatch(
      boardTemplatesApi.util.updateQueryData('getOwnerBoardTemplates', args, (draft) => draft.filter((item) => item.id !== id)),
    );
  }
  dispatch(boardTemplateRemoved({ id }));
};

async function onTemplateFulfilled(
  queryFulfilled: Promise<{ data: Template }>,
  dispatch: Dispatch,
  getState: () => unknown,
): Promise<void> {
  let data: Template;
  try {
    ({ data } = await queryFulfilled);
  } catch {
    // Ошибку показывает вызывающая сторона; кэш остаётся прежним.
    return;
  }
  putTemplateIntoCache(data, dispatch, getState);
}

// `ApiError` (`status?`, `message`) подходит под тип ошибки `graphqlBaseQuery` слайса — приведение не нужно.
const asApiError = (error: unknown) => ({ error: toApiError(error) });

/**
 * Шаблоны трансляций организации (Supabase `templates`) через RTK Query.
 *
 * Чтение идёт через `ensure_owner_templates`: у организации без шаблонов функция сначала копирует общий
 * набор, одной транзакцией (IMGM-1566). Кэш и дедупликация RTK Query дают один запрос в пути на все экраны
 * с одинаковыми аргументами — редактор табло и форма турнира читают одну запись. Мутации отдают шаблон
 * целиком и пишут его в кэш сами, тег не сбрасывают.
 *
 * `queryFn` поверх `supabase.overlay`, а не GraphQL-`baseQuery` слайса: данные живут не в бэкенде.
 */
export const boardTemplatesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getOwnerBoardTemplates: build.query<Template[], OwnerBoardTemplatesArgs>({
      async queryFn(args) {
        try {
          return { data: await supabase.overlay.ensureOwnerTemplates(args) };
        } catch (error) {
          return asApiError(error);
        }
      },
      providesTags: (_result, _error, { ownerId }) => [{ type: BOARD_TEMPLATE_TAG, id: ownerId }],
    }),

    /**
     * Табло шаблона для экспорта в файл (IMGM-1589). Табло правятся через слайс, этот кэш о правках не знает —
     * поэтому запись не живёт после отписки, а экспорт каждый раз запрашивает заново (lazy-запрос без `preferCacheValue`).
     */
    getBoardTemplateBoards: build.query<Board[], { templateId: number }>({
      async queryFn({ templateId }) {
        try {
          return { data: await supabase.overlay.getBoardsByTemplateId(templateId) };
        } catch (error) {
          return asApiError(error);
        }
      },
      keepUnusedDataFor: 0,
    }),

    createBoardTemplate: build.mutation<Template, CreateTemplate>({
      async queryFn(args) {
        try {
          return { data: await supabase.overlay.createTemplate(args) };
        } catch (error) {
          return asApiError(error);
        }
      },
      onQueryStarted: (_args, { dispatch, getState, queryFulfilled }) => onTemplateFulfilled(queryFulfilled, dispatch, getState),
    }),

    updateBoardTemplate: build.mutation<Template, UpdateTemplate>({
      async queryFn(args) {
        try {
          return { data: await supabase.overlay.updateTemplate(args) };
        } catch (error) {
          return asApiError(error);
        }
      },
      onQueryStarted: (_args, { dispatch, getState, queryFulfilled }) => onTemplateFulfilled(queryFulfilled, dispatch, getState),
    }),

    /** Дубликат своего шаблона и копия из каталога — одна операция `copyTemplate` с табло. */
    copyBoardTemplate: build.mutation<Template, CopyTemplate>({
      async queryFn(args) {
        try {
          return { data: await supabase.overlay.copyTemplate(args) };
        } catch (error) {
          return asApiError(error);
        }
      },
      onQueryStarted: (_args, { dispatch, getState, queryFulfilled }) => onTemplateFulfilled(queryFulfilled, dispatch, getState),
    }),

    /**
     * Новый шаблон из файла со всеми табло — одна транзакция `import_board_template` (IMGM-1589). Занятое название
     * сервер меняет на «<имя> (импорт)»; итоговое имя — в ответе.
     */
    importBoardTemplate: build.mutation<Template, ImportBoardTemplate>({
      async queryFn(args) {
        try {
          return { data: await supabase.overlay.importBoardTemplate(args) };
        } catch (error) {
          return asApiError(error);
        }
      },
      onQueryStarted: (_args, { dispatch, getState, queryFulfilled }) => onTemplateFulfilled(queryFulfilled, dispatch, getState),
    }),

    deleteBoardTemplate: build.mutation<{ id: number }, { id: number }>({
      async queryFn({ id }) {
        try {
          await supabase.overlay.deleteTemplate(id);
          return { data: { id } };
        } catch (error) {
          return asApiError(error);
        }
      },
      async onQueryStarted({ id }, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        removeBoardTemplateFromCache(id)(dispatch, getState);
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetOwnerBoardTemplatesQuery,
  useLazyGetBoardTemplateBoardsQuery,
  useCreateBoardTemplateMutation,
  useUpdateBoardTemplateMutation,
  useCopyBoardTemplateMutation,
  useImportBoardTemplateMutation,
  useDeleteBoardTemplateMutation,
} = boardTemplatesApi;
